import $ from 'jquery';
import FormHelper from './form-token-refresh/form-helper';
import { analytics } from './form-analytics';

// JQuery is only used to interact with intl-tel-input and select2

/**
 * Provides frontend validation to forms and enables submitting content via ajax.
 * Intended to be used with stage form module, although is usable as is.
 *
 * Features provided in this module:
 *
 * - Token refreshing with FormHelper
 * - Toggling form submit button(s), automatically and/or programmatically
 * - Validation
 * - Ajax submit option
 * - Custom events which are triggered on native form element. Event handlers can be registed with {@link Form.on} or with native addEventListener.
 *
 * If used without backend generated stageFormSettings, you have to provide required data your self:
 *
 * - Form must have id
 * - All inputs must have id
 * - If validation should be used, you must provide array of validators (id, validator type, error message) and validation rules object (field name mapped to array of validators).
 *
 * @see {@link https://nokiantyres.stage.crasman.fi/admin/manage/scriptlets/scriptletmanager/scriptleteditor/site/stagemodule/show/qNBZK4F_QSaeiP0Rcg3mXg Stage form module}
 * @see {@link https://nokiantyres.stage.crasman.fi/admin/manage/scriptlets/scriptletmanager/scriptleteditor/site/stagemodule/show/eJH_pjaxQnWSFA-mpRjwhg Stage form-validator module}
 * @see {@link FormHelper}
 */
class Form {

  #form = null;
  #formHelper = null;
  #options = {};
  #submitting = false;
  #fieldConditions = [];

  /**
   * Create new form.
   *
   * @param {HTMLFormElement} form Form element.
   * @param {object} options Form options.
   */
  constructor(form, options = {}) {
    this.#form = form;
    this.#formHelper = new FormHelper(form, {
      requestSubmit: true
    });
    const defaults = {
      errorClass: 'stage-error',
      validateBeforeSubmit: true,
      ajaxUrl: form.dataset.ajaxUrl || false,
      ajaxContentType: form.enctype || 'multipart/form-data',
      ajaxHeaders: {},
      ajaxMethod: form.method || false,
      gtmEventType: form.querySelector('[data-gtm-event-type]')?.value,
      gtmEventName: form.querySelector('[data-gtm-event-name]')?.value,
      validationError: '',
      validators: [],
      validationRules: {},
      ...((window.stageFormSettings ? window.stageFormSettings[form.id] : false) || {})
    };
    this.#options = { ...defaults, ...options };

    if (this.gtmEventName && this.gtmEventType) {
      console.log(`FormAnalytics init, data: event: ${this.gtmEventName}, form_campaign: ${this.gtmEventType}`)
    }
    this.toggleSubmits();

    // Initialize tel inputs without built-in validation or form logic
    if (form.querySelector('input[type="tel"]:not(.validateTel)')) {
      import(/* webpackChunkName: "intl-tel-input", webpackMode: "lazy" */'./intl-tel-input').then(m => { // jshint ignore:line
        m.default.init('input[type="tel"]:not(.validateTel)', {
          validate: false,
          useInternationalNumber: false
        });
      });
    }

    // Initialize select2 inputs which were not initialized already
    $(form).find('select:not(.select2):not(.select2-no-search)').each(function(){
      const select2Class = document.body.classList.contains('nht') ? 'nht-form-select2' : 'nt-form-select2';

      const select = this;
      let templateResult = undefined;
      let templateSelection = undefined;

      // Add color preview
      if (select.name === 'colorCode') {
        const colorOptionCallback = function (state) {
          if (!state.id || !state.element || !state.element.dataset.hex) {
            return state.text;
          }

          const $state = $(
            `<span class="select2-color-preview">
              <span class="select2-color-preview__color" style="background-color: ${state.element.dataset.hex};"></span>
              <span class="select2-color-preview__color-name">${state.text}</span>
            </span>`
          );

          return $state;
        };

        templateSelection = colorOptionCallback;
        templateResult = colorOptionCallback;
      }

      $(this).select2({
        minimumResultsForSearch: select.dataset.noSearch === 'true' ? Infinity : 5,
        width: '100%',
        templateResult,
        templateSelection,
        language: {
          noResults: function() {
            return select.dataset.noResultsText || 'No results';
          }
        }
      }).on('select2:open', function(e) {
        $('.select2-container--open').addClass(select2Class);
      });

      $(this.parentElement).find('.select2-container').addClass(select2Class);
    });

    // Add listeners
    const toggleListener = this.toggleSubmits.bind(this);

    // Revalidates invalid input
    const revalidate = (e) => {
      if (e.target.getAttribute('aria-invalid') === 'true' || e.target.classList.contains(this.#options.errorClass)) {
        const errors = this.validate();
        this.updateErrors(errors, {
          errorMessage: this.getOptions().validationError,
          invalidForm: Object.keys(errors).length > 0,
          focusFirstError: false
        });
      }
    };

    this.#form.querySelectorAll('input, textarea').forEach(input => {
      input.addEventListener('keyup', toggleListener);
      input.addEventListener('change', toggleListener);
      input.addEventListener('keyup', revalidate);
      input.addEventListener('blur', revalidate);
      input.addEventListener('input', () => {
        this.applyFieldConditions();
      });
    });

    this.#form.querySelectorAll('select').forEach(select => {
      $(select).on('change', () => {
        this.toggleSubmits();
        this.applyFieldConditions();
      });
      select.addEventListener('blur', revalidate);
    });

    this.#form.addEventListener('formhelper.fetchToken', (e) => {
      this.toggleSubmits(false);
      this.toggleLoadingIndicator(true);
    });
    this.#form.addEventListener('formhelper.tokenFetched', (e) => {
      this.toggleSubmits();
      this.toggleLoadingIndicator(false);
    });

    this.#form.addEventListener('submit', (e) => {
      if (e.defaultPrevented) {
        return;
      }

      e.preventDefault();
      this.submit();
    });

    // Browser does not support requestSubmit, so just redo it manually
    this.#form.addEventListener('formhelper.requestSubmit', (e) => {
      this.submit();
    });

    this.applyFieldConditions();
  }

  /**
   * Toggles submit buttons based on given value or whether form can be submitted or not.
   *
   * @param {boolean|null} enabled If given, submits are forced to given state. Otherwise value is result of canSubmit().
   * @see {@link canSubmit} For details how submit status is determined.
   */
  toggleSubmits(enabled = null) {
    const disabled = typeof enabled === 'boolean' ? !enabled : !this.canSubmit();
    this.#form.querySelectorAll('[type="submit"]').forEach(button => {
      button.disabled = disabled;
    });
  }

  /**
   * Toggles loading indicators.
   *
   * @param {boolean} enabled If true, form loading indicator is shown.
   */
  toggleLoadingIndicator(enabled = true) {
    this.#form.querySelectorAll('[type="submit"]').forEach(button => {
      if (enabled) {
        button.classList.add('loading');
      }
      else {
        button.classList.remove('loading');
      }
    });
  }

  /**
   * Run all field condition rules.
   */
  applyFieldConditions() {
    this.getFieldConditions().forEach(condition => {
      const formData = this.getFormData();
      const target = this.getForm().querySelectorAll(`[name=${condition.target}]`);
      if (target.length === 0) {
        return;
      }

      // Check if all rules match
      const allMatch = condition.rules.filter(rule => {
        const value = formData.get(rule.field) || '';
        if (rule.comparison === 'empty') {
          return value.length === 0;
        }
        else if (rule.comparison === 'notempty') {
          return value.length > 0;
        }
        else if (rule.comparison === 'is') {
          const ruleValue = Array.isArray(rule.value) ? rule.value : [rule.value];
          return ruleValue.includes(value);
        }
        else if (rule.comparison === 'not') {
          const ruleValue = Array.isArray(rule.value) ? rule.value : [rule.value];
          return !ruleValue.includes(value);
        }
      }).length === condition.rules.length;

      // Run actions with resolved value
      condition.actions.forEach(action => {
        target.forEach(targetEl => {
          if (action === 'disable') {
            targetEl.disabled = allMatch;

            if (!allMatch) {
              targetEl.closest('fieldset').disabled = false;
            }
          }
          else if (action === 'hide') {
            targetEl.closest('[data-field]').hidden = allMatch;
          }
        });
      });
    });
    this.toggleSubmits();
  }

  /**
   * Set all field conditions. Field conditions are used to disable and/or hide input based on state of other inputs in form.
   *
   * @param {object[]} conditions Array of conditions.
   * @param {string} conditions.target Field where conditions apply to.
   * @param {string[]} conditions.actions Array of actions run on target field if rules match. Available options: disable, hide.
   * @param {object[]} conditions.rules Array of condition rules which all must match.
   * @param {string} conditions.rules.field Name of field which value is compared.
   * @param {string} conditions.rules.comparison Comparison run against field. Options are: empty, notempty, is, not
   * @param {string[]|string} [conditions.rules.value] Values for match if comparison rule is "is" or "not".
   */
  setFieldConditions(conditions) {
    this.#fieldConditions = conditions;
    this.applyFieldConditions();
  }

  /**
   * Get all existing field conditions.
   *
   * @returns {object[]} Array of conditions.
   */
  getFieldConditions() {
    return this.#fieldConditions;
  }

  /**
   * Updates form field invalid status and error message.
   *
   * @param {HTMLInputElement} input Input element which error is handled.
   * @param {string} message Error message. If empty, field is considered valid and error is hidden.
   * @param {object} [options] Optional options
   * @param {string} [options.errorClass] Input error class to toggle. Defaults to form errorClass.
   * @param {string} [options.errorCssDisplay] Css display style, defaults to 'block'. If empty, error message element display style is not updated.
   */
  updateFieldError(input, message = '', options = {}) {
    options = {
      errorClass: this.getOptions().errorClass,
      errorCssDisplay: 'block',
      ...options
    };

    const container = input.closest('[data-field]');
    if (!container) {
      return;
    }

    const inputId = container.dataset.field || input.id;
    const invalid = !!message;
    const describedby = input.getAttribute('aria-describedby') ? input.getAttribute('aria-describedby').split(' ') : [];
    const errorIndex = describedby.findIndex(id => id === inputId + '-error');
    const errorElement = this.#form.querySelector(`#${inputId}-error`);

    if (invalid) {
      input.classList.add(options.errorClass);
      input.setAttribute('aria-invalid', 'true');
      if (errorIndex === -1) {
        describedby.push(inputId + '-error');
      }
      if (container) {
        container.classList.add(options.errorClass);
      }
    }
    else {
      input.classList.remove(options.errorClass);
      input.setAttribute('aria-invalid', 'false');
      if (errorIndex > -1) {
        describedby.slice(errorIndex, 1);
      }
      if (container) {
        container.classList.remove(options.errorClass);
      }
    }

    if (errorElement) {
      errorElement.innerText = message;

      if (options.errorCssDisplay) {
        if (invalid) {
          errorElement.style.display = options.errorCssDisplay;
        }
        else {
          errorElement.style.display = 'none';
        }
      }
    }

    if (errorElement && describedby.length > 0) {
      input.setAttribute('aria-describedby', describedby.join(' '));
    }
    else {
      input.removeAttribute('aria-describedby');
    }
  }

  /**
   * Updates form and form field invalid status an error message.
   *
   * @param {object} [errors] Field names mapped to error message. If empty, form is considered valid.
   * @param {object} [options] Options
   * @param {string} [options.errorClass] Form error class, defaults to one set in options.
   * @param {boolean} [options.invalidForm] Whether form is considered invalid. Defaults to true if errors is non-empty object.
   * @param {string} [options.errorMessage] Form error message. If empty, message is hidden. Default is empty string.
   * @param {boolean} [options.focusFirstError] Whether focus is moved to first invalid field. Defaults to true.
   * @param {string} [options.fieldErrorClass] Input error class to toggle. Defaults to form errorClass.
   * @param {string} [options.fieldErrorCssDisplay] Css display style, defaults to 'block'.
   * @see {@link updateFieldError} For more details about how field errors are handled
   */
  updateErrors(errors = {}, options = {}) {
    options = {
      // Form options
      errorClass: this.getOptions().errorClass,
      invalidForm: Object.keys(errors).length > 0,
      errorMessage: '',
      focusFirstError: true,

      // Field options
      fieldErrorClass: this.getOptions().errorClass,
      fieldErrorCssDisplay: 'block',

      ...options,
    };

    const formError = this.#form.querySelector(`#${this.#form.id}-error`);
    const inputs = this.#form.querySelectorAll('input, select, textarea');
    if (options.invalidForm) {
      this.#form.classList.add(options.errorClass);

      if (formError) {
        if (options.errorMessage) {
          formError.innerText = options.errorMessage || '';
          formError.style.display = 'block';
        }
        else {
          formError.style.display = 'none';
        }
      }
    }
    else {
      this.#form.classList.remove(options.errorClass);
      if (formError) {
        formError.style.display = 'none';
      }
    }

    // Update field errors
    inputs.forEach(input => {
      this.updateFieldError(input, errors[input.name] || '', {
        errorClass: options.fieldErrorClass,
        errorCssDisplay: options.fieldErrorCssDisplay,
      });
    });

    // Moves focus to first non-hidden field
    if (options.focusFirstError) {
      const input = Array.from(inputs).find(el => el.type !== 'hidden' && el.getAttribute('aria-invalid') === 'true');
      if (input) {
        input.focus();
      }
    }
  }

  /**
   * Creates and dispatches new custom event on form element.
   *
   * @param {string} name Event name.
   * @param {object} data Event data.
   * @param {boolean} cancelable Whether event can be canceled. Defaults to true.
   * @returns {CustomEvent} Created event.
   */
  #dispatchEvent(name, data = {}, cancelable = true) {
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable,
      detail: {
        form: this,
        ...data
      }
    });
    this.#form.dispatchEvent(event);
    return event;
  }

  /**
   * Register event handler for custom events.
   *
   *  - `form.validateBeforeSubmit`: Triggered before form is validated, cancelable
   *  - `form.beforeSubmit`: Triggered before submit and after validation, cancelable
   *  - `form.ajaxResponse`: Triggered after ajax response is received and processed internally
   *
   * @param {string} event Event name.
   * @param {function} callback Callback data which receives object containing native event detail.
   */
  on(event, callback) {
    // Add internal prefix if it was not provided
    event = event.indexOf('form.') !== 0 ? 'form.' + event : event;
    this.getForm().addEventListener(event, (e) => {
      callback(e.detail);
    });
  }

  /**
   * Get form options.
   *
   * @returns {object}
   */
  getOptions() {
    return this.#options;
  }

  /**
   * Get form element.
   *
   * @returns {HTMLFormElement}
   */
  getForm() {
    return this.#form;
  }

  /**
   * Get form helper which handles tokens.
   *
   * @returns {FormHelper}
   */
  getFormHelper() {
    return this.#formHelper;
  }

  /**
   * Get form data.
   *
   * @returns {FormData}
   */
  getFormData() {
    const data = new FormData(this.#form);
    $(this.getForm()).find('input[type="tel"]').each(function() {
      const $el = $(this);
      if (typeof $el.intlTelInput === 'function') {
        data.set(this.name, $el.intlTelInput('getNumber') || this.value);
      }
    });
    return data;
  }

  /**
   * Checks whether form can be submitted. Form can be submitted if all required fields are filled.
   *
   * Note! Disabled fields are ignored even if they are flagged as required.
   *
   * @returns {boolean} True if all required fields were provided.
   */
  canSubmit() {
    const validators = this.getOptions().validationRules;
    const fieldsToValidate = this.#getFieldNamesForValidation();
    const requiredFields = fieldsToValidate.filter(name => validators[name].includes('required'));

    // Check if all required fields have been filled
    const formData = this.getFormData();
    let canSubmit = true;
    requiredFields.forEach(field => {
      const value = formData.getAll(field);
      if (value.length === 0 || !value[0]) {
        canSubmit = false;
      }
    });

    return canSubmit;
  }

  /**
   * Sends form data with ajax.
   *
   * @param {string} url Ajax url.
   * @param {object} params Parameters for native fetch.
   * @returns {Promise} Fetch json result.
   */
  #sendAjax(url, params) {
    const completed = () => {
      this.toggleSubmits();
      this.toggleLoadingIndicator(false);
      this.#submitting = false;
      this.#formHelper.enableOnSubmitUpdate();
    };
    return fetch(url, params)
      .then(response => response.json())
      .then(data => {
        completed();

        // If response is from api (app-core), use result object
        data = data.code && data.status && typeof data.result === 'object' ? data.result : data;

        // Reset tokens
        if (data.token) {
          this.#formHelper.setToken(data.token);
        }

        // Render errors
        if (!data.success) {
          this.updateErrors(data.fields || {}, {
            errorMessage: data.message,
            invalidForm: true,
            focusFirstError: true
          });
        }
        else {
          this.updateErrors({});
          if (this.gtmEventName && this.gtmEventType) {
            analytics.sendToGTM({ eventType: this.gtmEventType, eventName: this.gtmEventName });
          }
        }

        this.#dispatchEvent('form.ajaxResponse', { data }, false);

        // Disable submit buttons and redirect
        if (data.redirect) {
          console.log('Redirect to: ', data.redirect);
          this.toggleSubmits(false);
          window.location = data.redirect;
        }
      })
      .catch(() => {
        completed();

        this.#dispatchEvent('form.ajaxResponse', { data: null }, false);
      });
  }

  /**
   * Validates and submits form.
   */
  submit() {
    if (this.#submitting) {
      return;
    }

    this.#submitting = true;

    // Disable submit buttons
    this.toggleSubmits(false);
    this.toggleLoadingIndicator(true);

    const data = this.getFormData();
    const options = this.getOptions();

    // Validate submitted data
    if (options.validateBeforeSubmit) {
      const validationResult = this.validate(data);
      const validationEvent = this.#dispatchEvent('form.validateBeforeSubmit', { data, validationResult });
      console.log(validationResult, validationEvent);

      if (!validationEvent.defaultPrevented) {
        if (Object.keys(validationResult).length > 0) {
          this.updateErrors(validationResult, {
            errorMessage: options.validationError,
            invalidForm: true,
            focusFirstError: true
          });
          this.#submitting = false;
          this.toggleSubmits();
          this.toggleLoadingIndicator(false);
          return;
        }
      }
    }

    // Ajax settings for fetch
    const ajaxBaseUrl = new URL(window.location.protocol + '//' + window.location.host);
    const ajax = !options.ajaxUrl ? null : {
      url: new URL(options.ajaxUrl, ajaxBaseUrl),
      method: (options.ajaxMethod || this.#form.method || 'get').toLowerCase(),
      headers: options.ajaxHeaders,
      body: data
    };

    if (ajax) {
      if (ajax.method === 'get') {
        for (let item of ajax.body) {
          ajax.url.searchParams.append(item[0], item[1]);
        }
        ajax.body = null;
      }

      // Override content-type and x-requested-with headers
      ajax.headers['Content-Type'] = options.ajaxContentType;
      ajax.headers['X-Requested-With'] = 'XMLHttpRequest';

      // Convert FormData to json
      if (options.ajaxContentType === 'application/json') {
        ajax.body = JSON.stringify(Object.fromEntries(ajax.body));
      }
      // Convert FormData to url string
      else if (options.ajaxContentType === 'application/x-www-form-urlencoded') {
        const params = new URLSearchParams();
        for (let [key, value] of ajax.body) {
          params.append(key, value);
        }
        ajax.body = params.toString();
      }
      // Delete content type header, browser will set it to multipart with correct boundary value
      else if (options.ajaxContentType === 'multipart/form-data') {
        delete ajax.headers['Content-Type'];
      }

      console.log(ajax);
    }

    const beforeSubmitEvent = this.#dispatchEvent('form.beforeSubmit', { ajax, data });
    if (beforeSubmitEvent.defaultPrevented) {
      this.#submitting = false;
      return;
    }

    if (!ajax) {
      if (this.gtmEventType && this.gtmEventName) {
        analytics.queueStageFormSubmitEvent(this.gtmEventType, this.gtmEventName);
      }
      this.#form.submit();
    }
    else {
      const url = ajax.url;
      delete ajax.url;
      this.#sendAjax(url, ajax);
    }
  }

  /**
   * Get field names that can be validated. Excludes disabled fields and fields without configured validators.
   *
   * @returns {string[]} Array of field names.
   */
  #getFieldNamesForValidation() {
    const fieldsToValidate = [];
    if (this.getOptions().validationRules) {
      for (let name in this.getOptions().validationRules) {
        const input = this.getForm().querySelectorAll(`[name=${name}]`);
        const excludedInputs = Array.from(input).filter(item => item.disabled);
        if (excludedInputs.length < input.length) {
          fieldsToValidate.push(name);
        }
      }
    }
    return fieldsToValidate;
  }

  /**
   * Validates form data.
   *
   * @param {FormData} [formData=null] FormData object. Defaults to this.getFormData().
   * @returns {object} Invalid field names and error message.
   */
  validate(formData = null) {
    const options = this.getOptions();
    const errors = {};
    formData = formData || this.getFormData();

    if (options.validationRules) {
      const fieldsToValidate = this.#getFieldNamesForValidation();
      fieldsToValidate.forEach(name => {
        const inputValidators = options.validationRules[name];
        const value = formData.getAll(name);

        const skipValidation = value.length <= 1 && (value.length === 0 || (!value[0] && value[0] !== 0)) && !inputValidators.includes('required');
        if (skipValidation) {
          return;
        }

        for (let i = 0; i < inputValidators.length; i++) {
          const parts = inputValidators[i].split(':');
          const id = parts[0];
          const data = parts.length > 1 ? parts[1].split(',') : null;
          const validator = options.validators.find(item => item.id === id);
          if (validator) {
            try {
              if (validator.validator === 'tel') {
                const error = $(this.getForm()).find(`input[name="${name}"]`).intlTelInput('getValidationError');
                if (error !== 0) {
                  errors[name] = validator.message;
                  break;
                }
              }
              else {
                const result = validate(validator.validator, value, data);
                if (!result) {
                  errors[name] = validator.message.split('%value%').join(data ? data.join(', ') : '');
                  break;
                }
              }
            }
            catch(e) {
              // Do nothing
            }
          }
        }
      });
    }

    return errors;
  }
}

/**
 * Validates given value against given validator.
 *
 * @param {string} id Validator id.
 * @param {array|string|number} value Value or array of values.
 * @param {array|null} data Validator options.
 * @returns {boolean} True if value is valid.
 * @throws {Error} If validator is unknown or incorrect data was provided to validator.
 */
function validate(id, value, data = null) {
  value = Array.isArray(value) ? value : [value];
  switch(id) {
  case 'required':
    return value.length > 1 || (!!value[0] && value[0] !== 0);
  case 'email':
    return value.every(val => String(val).trim().match(/.+@.+/));
  case 'int':
    return value.every(val => String(val) === String(parseInt(val)));
  case 'decimal': {
    return value.every(val => {
      const parts = String(val).replace(',', '.').split('.');
      const part0 = parts[0].match(/^-?[\d]+$/);
      const part1 = parts.length > 1 ? parts[1].match(/^[\d]+$/) : true;
      return part0 && part1 && parts.length <= 2;
    });
  }
  case 'in': {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error(`Allowed values not provided for validaton rule "${id}"`);
    }
    return value.every(val => data.includes(val));
  }
  case 'min': {
    const min = parseFloat(data[0]);
    if (isNaN(min)) {
      throw new Error('Invalid min value');
    }
    return value.every(val => parseFloat(val) >= min);
  }
  case 'max': {
    const max = parseFloat(data[0]);
    if (isNaN(max)) {
      throw new Error('Invalid max value');
    }
    return value.every(val => parseFloat(val) <= max);
  }
  case 'minlength': {
    const min = parseInt(data[0]);
    if (isNaN(min) || min < 0) {
      throw new Error('Invalid minlength value');
    }
    return value.every(val => String(val).length >= min);
  }
  case 'maxlength': {
    const max = parseInt(data[0]);
    if (isNaN(max) || max < 1) {
      throw new Error('Invalid maxlength value');
    }
    return value.every(val => String(val).length <= max);
  }
  }

  throw new Error(`Unknown validator "${id}"`);
}

export default Form;
export { validate };
